import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Check,
  ChevronDown,
  ExternalLink,
  Loader2,
  Lock,
  LogOut,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '@mesterplan/auth-react';
import { useApiQuery, useMesterplanApi } from '@mesterplan/web-hooks';
import type { CompanyLockLike, CompanyLockReasonLike, MyCompanyLike } from '@mesterplan/web-hooks';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** What the app root knows about the lock: the 423 body minus its `error` tag. */
export interface CompanyLockView {
  reason: CompanyLockReasonLike | null;
  /** Where the Trackrs owner switches Mesterplan back on. */
  reenableUrl: string;
}

export interface LockedCompanyOption {
  id: string;
  name: string;
}

interface LockedCompanyScreenProps {
  /** The locked company. */
  activeOrgId: string;
  lock: CompanyLockView;
  /** Every company the user is a member of; the switcher shows when there is more than one. */
  companies: ReadonlyArray<LockedCompanyOption>;
  onSwitchCompany: (orgId: string) => void;
  /**
   * The `awaiting_billing` poll saw a change: unlocked (`locked: false`), or locked
   * for another reason. The caller owns what happens next (reload the org state,
   * or show the new reason).
   */
  onLockResolved: (lock: CompanyLockLike) => void;
}

/** `awaiting_billing` polls `GET companies/current/lock` this often ... */
export const LOCK_POLL_INTERVAL_MS = 3_000;
/** ... for this long before giving up and offering a retry. */
export const LOCK_POLL_TIMEOUT_MS = 60_000;
const LOCK_POLL_MAX_ATTEMPTS = Math.floor(LOCK_POLL_TIMEOUT_MS / LOCK_POLL_INTERVAL_MS);

const FALLBACK_COMPANY_NAME = 'jeres virksomhed';

function isAdminRole(role: string | undefined): boolean {
  const normalised = role?.toLowerCase();
  return normalised === 'owner' || normalised === 'admin';
}

/**
 * The company lock (M9). Rendered by `NetClientsProvider` in place of the app
 * when the active company is locked: either `GET companies/current/lock` said so,
 * or an org-scoped call answered 423 `company_locked`.
 *
 * `module_cancelled`: the Trackrs owner switched Mesterplan off. Data is kept; the
 * owner switches it back on in Trackrs. Admins get the link, everyone else is
 * pointed at their administrator.
 *
 * `awaiting_billing`: Mesterplan was just enabled from Trackrs and the billing
 * state has not arrived yet. Polls every 3 s for up to 60 s, then offers a retry.
 *
 * The app's providers (`CompanyProvider`, `NetUserProvider`) are NOT mounted
 * under this screen, so the membership role and the company list come from the
 * allow-listed `users.myCompanies` and from the caller respectively.
 */
export function LockedCompanyScreen({
  activeOrgId,
  lock,
  companies,
  onSwitchCompany,
  onLockResolved,
}: LockedCompanyScreenProps) {
  const api = useMesterplanApi();
  const { logout } = useAuth();

  // The membership role for the active company. `/api/users/me/*` is reachable
  // while locked, which is what makes this screen self-sufficient.
  const membershipsQuery = useApiQuery<MyCompanyLike[]>(() => api.users.myCompanies(), [api]);
  const membership = useMemo(
    () =>
      membershipsQuery.status === 'success'
        ? membershipsQuery.data?.find((row) => row.id === activeOrgId)
        : undefined,
    [membershipsQuery.status, membershipsQuery.data, activeOrgId],
  );
  // Until the role is known, say nothing role-specific rather than flash the
  // wrong line. A failed read falls through to the "contact your admin" copy.
  const roleKnown = membershipsQuery.status === 'success' || membershipsQuery.status === 'error';
  const isAdmin = isAdminRole(membership?.role);

  const companyName =
    companies.find((company) => company.id === activeOrgId)?.name ?? membership?.name ?? FALLBACK_COMPANY_NAME;

  const awaitingBilling = lock.reason === 'awaiting_billing';

  // ── awaiting_billing poll ─────────────────────────────────────────────────
  const [pollState, setPollState] = useState<'polling' | 'timeout'>('polling');
  const [pollRun, setPollRun] = useState(0);
  const retryPoll = useCallback(() => {
    setPollState('polling');
    setPollRun((run) => run + 1);
  }, []);

  useEffect(() => {
    if (!awaitingBilling || pollState !== 'polling') return;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      attempts += 1;
      try {
        const res = await api.companies.currentLock();
        if (cancelled) return;
        if (res.ok && (!res.data.locked || res.data.reason !== 'awaiting_billing')) {
          onLockResolved(res.data);
          return;
        }
      } catch {
        // A network blip is not a verdict; keep polling until the window closes.
      }
      if (cancelled) return;
      if (attempts >= LOCK_POLL_MAX_ATTEMPTS) {
        setPollState('timeout');
        return;
      }
      timer = setTimeout(() => void tick(), LOCK_POLL_INTERVAL_MS);
    };

    // The caller has just read the lock, so the first re-read waits a full interval.
    timer = setTimeout(() => void tick(), LOCK_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [api, awaitingBilling, pollState, pollRun, onLockResolved]);

  const showSwitcher = companies.length > 1;

  return (
    <div className="flex min-h-screen w-full flex-col bg-white dark:bg-dark-card lg:flex-row">
      {/* ── Branded panel ────────────────────────────────────────────── */}
      <aside className="relative isolate flex flex-col justify-between overflow-hidden bg-gradient-to-br from-primary-700 via-primary to-secondary px-8 py-10 text-white lg:w-[44%] lg:px-12 lg:py-14">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18]"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '26px 26px',
            maskImage: 'radial-gradient(120% 70% at 75% 0%, black 35%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(120% 70% at 75% 0%, black 35%, transparent 80%)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-20 -z-10 h-64 w-64 rounded-full bg-white/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-10 -z-10 h-64 w-64 rounded-full bg-secondary/40 blur-3xl"
        />

        <div className="animate-in fade-in slide-in-from-left-2 duration-500">
          <div className="mb-7 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/25 bg-white/15 shadow-lg backdrop-blur-sm">
            {awaitingBilling ? (
              <Loader2 className="h-7 w-7 animate-spin" strokeWidth={2} />
            ) : (
              <Lock className="h-7 w-7" strokeWidth={2} />
            )}
          </div>
          <p className="text-[13px] font-medium uppercase tracking-[0.16em] text-white/70">Mesterplan</p>
          {awaitingBilling ? (
            <>
              <h1 className="mt-2 max-w-[15ch] text-3xl font-bold leading-[1.15] text-white lg:text-4xl">
                Mesterplan gøres klar…
              </h1>
              <p className="mt-4 max-w-[34ch] text-[15px] leading-relaxed text-white/85">
                Vi gør Mesterplan klar til {companyName}. Det tager normalt under et minut.
              </p>
            </>
          ) : (
            <>
              <h1 className="mt-2 max-w-[18ch] text-3xl font-bold leading-[1.15] text-white lg:text-4xl">
                Mesterplan er slået fra for {companyName}
              </h1>
              <p className="mt-4 max-w-[34ch] text-[15px] leading-relaxed text-white/85">
                Jeres sager, tilbud og timer er gemt og venter på jer.
              </p>
            </>
          )}
        </div>

        <p className="mt-10 text-[13px] text-white/60 animate-in fade-in slide-in-from-left-2 duration-500 delay-150">
          Adgangen følger Mesterplan-udvidelsen i Trackrs.
        </p>
      </aside>

      {/* ── Action panel ─────────────────────────────────────────────── */}
      <main className="flex flex-1 items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100">
          {awaitingBilling ? (
            <AwaitingBillingPanel pollState={pollState} onRetry={retryPoll} />
          ) : (
            <CancelledPanel roleKnown={roleKnown} isAdmin={isAdmin} reenableUrl={lock.reenableUrl} />
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-bordergray pt-6 dark:border-dark-border">
            {showSwitcher && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" className="gap-2">
                    <Building2 className="h-4 w-4 text-muted dark:text-dark-muted" />
                    Skift virksomhed
                    <ChevronDown className="h-3.5 w-3.5 text-muted dark:text-dark-muted" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  {companies.map((company) => (
                    <DropdownMenuItem
                      key={company.id}
                      onClick={() => {
                        if (company.id !== activeOrgId) onSwitchCompany(company.id);
                      }}
                      className="flex items-center gap-2"
                    >
                      <Building2 className="h-4 w-4 text-muted dark:text-dark-muted" />
                      <span className="flex-1 truncate">{company.name}</span>
                      {company.id === activeOrgId && <Check className="h-4 w-4 text-primary" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button type="button" variant="ghost" className="gap-2" onClick={() => void logout()}>
              <LogOut className="h-4 w-4" />
              Log ud
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

function CancelledPanel({
  roleKnown,
  isAdmin,
  reenableUrl,
}: {
  roleKnown: boolean;
  isAdmin: boolean;
  reenableUrl: string;
}) {
  return (
    <>
      <h2 className="text-xl font-semibold text-dark dark:text-dark-text">Sådan kommer I i gang igen</h2>
      {/* Keep the line's height while the role loads, so the panel does not jump. */}
      <div className="mt-1 min-h-[3.25rem]">
        {roleKnown && isAdmin && (
          <p className="text-[14px] leading-relaxed text-muted dark:text-dark-muted">
            Ejeren kan slå Mesterplan til igen i Trackrs → Udvidelser
          </p>
        )}
        {roleKnown && !isAdmin && (
          <p className="text-[14px] leading-relaxed text-muted dark:text-dark-muted">Kontakt jeres administrator</p>
        )}
      </div>
      {roleKnown && isAdmin && (
        <a
          href={reenableUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-6 inline-flex h-[48px] w-full items-center justify-center gap-2 whitespace-nowrap rounded-sm bg-primary px-6 py-3 text-[14px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2"
        >
          Åbn Trackrs → Udvidelser
          <ExternalLink className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </a>
      )}
    </>
  );
}

function AwaitingBillingPanel({
  pollState,
  onRetry,
}: {
  pollState: 'polling' | 'timeout';
  onRetry: () => void;
}) {
  if (pollState === 'timeout') {
    return (
      <div role="status" aria-live="polite">
        <h2 className="text-xl font-semibold text-dark dark:text-dark-text">Ikke klar endnu</h2>
        <p className="mt-1 text-[14px] leading-relaxed text-muted dark:text-dark-muted">
          Det tager længere end ventet — prøv igen om lidt
        </p>
        <Button type="button" size="lg" className="mt-6 w-full gap-2" onClick={onRetry}>
          <RefreshCw className="h-4 w-4" />
          Prøv igen
        </Button>
      </div>
    );
  }
  return (
    <div role="status" aria-live="polite" className="flex items-start gap-3.5">
      <Loader2 className="mt-0.5 h-5 w-5 flex-shrink-0 animate-spin text-primary" />
      <div>
        <h2 className="text-xl font-semibold text-dark dark:text-dark-text">Gør klar…</h2>
        <p className="mt-1 text-[14px] leading-relaxed text-muted dark:text-dark-muted">
          Siden åbner af sig selv, så snart alt er på plads.
        </p>
      </div>
    </div>
  );
}
